/**
 * Accepted: the agent turns round to face them (the not-yet shot) and says
 * they are on the clock and that the mission is coming by text; below, their
 * case lies open, the file and the recorder in it, with the 24 hours already
 * running on the screen in its lid.
 * "On it" moves on to the live mission.
 */
import { useEffect, useState, type CSSProperties } from "react";
import type { Tier } from "@/features/funnel/types";
import { useFunnelStore } from "@/store";
import { ACTION_BTN_CLASS } from "./step-shell";
import { PHOTO_BOX, bubbleFor, notYetFor } from "./desk-art";
import { CASE_LIGHT, FRONT_WALL_CLIP, OPEN_CASES } from "./open-case";
import { Laptop } from "./laptop";
import { LidClock, WINDOW_MS } from "./lid-clock";
import { ChevronDown } from "lucide-react";

/** How long they can sit here before we point at the button. */
const HINT_MS = 7000;

export function OnTheClock({
  handler,
  tier,
  message,
  button,
  onGo,
}: {
  handler: string | null;
  tier: Tier;
  message: string;
  button: string;
  onGo: () => void;
}) {
  // the clock started the moment they accepted (and the live mission
  // counts down from the same moment)
  const [since] = useState(
    () => useFunnelStore.getState().clockFrom ?? Date.now(),
  );
  const [now, setNow] = useState(since);
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, []);
  const bubble = bubbleFor(handler);
  // still here: point at the button
  const [hint, setHint] = useState(false);
  useEffect(() => {
    const t = window.setTimeout(() => setHint(true), HINT_MS);
    return () => window.clearTimeout(t);
  }, []);

  return (
    <div
      style={{ "--sm-ring": CASE_LIGHT[tier] } as CSSProperties}
      className="absolute inset-0 z-[8] animate-[smFadeIn_.5s_ease_both] overflow-clip bg-[var(--sm-bg)]"
    >
      <img
        src={notYetFor(handler)}
        alt=""
        className="absolute inset-0 h-full w-full object-cover object-[50%_100%]"
      />
      {/* what they say, off their mouth (as when the sealed case is waved
          off) */}
      <div
        style={PHOTO_BOX}
        // over the case where they meet (the handlers' bubbles sit lower)
        className="pointer-events-none absolute bottom-0 left-1/2 z-[3] -translate-x-1/2"
      >
        <div
          style={{ top: bubble.top }}
          className="absolute right-0 left-0 z-[2] flex justify-center"
        >
          <div className="relative w-[min(380px,calc(100vw-40px))] animate-[smSay_.45s_cubic-bezier(.2,.8,.3,1)_.3s_both] rounded-[6px] bg-[#f3ecd8] px-5 py-4 text-center text-[#2b2107] shadow-[0_22px_50px_rgba(0,0,0,.75)]">
            {handler ? (
              <div className="mb-1.5 font-mono text-[10px] tracking-[.24em] text-[#8a7350] uppercase">
                {handler}
              </div>
            ) : null}
            <div className="text-[clamp(13px,3.9vw,16px)] leading-[1.32] font-medium text-pretty">
              {message}
            </div>
            <div
              style={{ left: bubble.tailLeft }}
              className="absolute -top-[26px] size-0 -translate-x-1/2 border-x-[11px] border-b-[27px] border-x-transparent border-b-[#f3ecd8]"
            />
          </div>
        </div>
      </div>

      {/* on the desk, in front: their case, open, the clock running in its
          lid; and the way on */}
      <div className="absolute inset-x-0 bottom-[max(20px,4dvh)] z-[1] flex flex-col items-center gap-[2.5dvh] px-3">
        <div className="relative aspect-[1451/1084] w-[min(140vw,600px,59dvh)] animate-[smSay_.5s_cubic-bezier(.2,.8,.3,1)_.15s_both] [container-type:inline-size]">
          <img
            src={OPEN_CASES[tier]}
            alt=""
            className="absolute inset-0 size-full drop-shadow-[0_16px_16px_rgba(0,0,0,.8)]"
          />
          {/* the laptop, back in the case (the live mission opens it) */}
          <Laptop tier={tier} />
          <LidClock tier={tier} left={WINDOW_MS - (now - since)} />
          <img
            src={OPEN_CASES[tier]}
            alt=""
            style={{ clipPath: FRONT_WALL_CLIP }}
            className="pointer-events-none absolute inset-0 z-[2] size-full"
          />
        </div>
        <button
          type="button"
          onClick={onGo}
          className={`${ACTION_BTN_CLASS} animate-[smSay_.45s_cubic-bezier(.2,.8,.3,1)_.6s_both] rounded-[9px] px-10 py-3 text-[16px] font-bold`}
        >
          {button}
        </button>
      </div>
      {hint ? (
        // over the case's lid, just above the button
        <div className="pointer-events-none absolute inset-x-0 bottom-[calc(max(20px,4dvh)+64px)] z-[4] flex animate-[smSay_.45s_cubic-bezier(.2,.8,.3,1)_both] flex-col items-center gap-1">
          <span className="rounded-full border border-[#d6a94e]/60 bg-black/80 px-4 py-2 font-stencil text-[12.5px] tracking-[2.5px] text-[#eadfc6] uppercase shadow-[0_6px_16px_rgba(0,0,0,.6)]">
            Tap <span className="text-[#ff8a7a]">{button}</span> to continue
          </span>
          <ChevronDown
            strokeWidth={3}
            className="size-7 animate-[smHintBob_1.1s_ease-in-out_infinite] text-[#d6a94e] [filter:drop-shadow(0_0_8px_#d6a94e)]"
          />
        </div>
      ) : null}
      <style>{`
        @keyframes smFadeIn{from{opacity:0}to{opacity:1}}
        @keyframes smHintBob{0%,100%{translate:0 0}50%{translate:0 30%}}
        @keyframes smSay{from{opacity:0;transform:translateY(10px) scale(.96)}to{opacity:1;transform:none}}
      `}</style>
    </div>
  );
}
